import { useParams } from 'react-router-dom';
import { useTenantGet } from '../hooks/use-tenant.hook';
import { memo, useMemo } from 'react';
import TenantDetailsComponent from '../components/tenant-details.component';
import { DetailItem } from '../../../../common/RenderDetails';
import { BrandingColors, Tenant } from '../types/tenant.types';
import HeaderRow from '../../../../common/HeaderRow';
import { useTranslation } from 'react-i18next';
import ErrorFallback from '../../../../common/ErrorFallback';
import { ExclamationCircleOutlined } from '@ant-design/icons';
import { Tag } from 'antd';
import dayjs from 'dayjs';
import { Country } from '../../../sysadmin/countries/types/country.types';
import { WithPermissionCheck } from '../../../../auth/WithPermissionCheck';
import { useActiveTenant } from '../../../../../states/active-tenant.atom';
import { formatDate } from '@utils/date.util';
import MediaPreview from '@components/common/MediaPreview';
import { rbac } from 'permissions/permissions';
import { useAuthUser } from '@states/user.atom';
import PageTitle from '@components/common/PageTitle';

const TenantDetailsContainer = () => {
  const { activeTenant } = useActiveTenant();
  const { t } = useTranslation();
  const { getUserPermissions } = useAuthUser();
  const userPermissions = getUserPermissions();
  const id = useMemo(() => {
    return activeTenant?.id;
  }, [activeTenant]);
  const { data, isLoading, error } = useTenantGet(id || '');

  const prepareDetailsData = useMemo(
    () =>
      (dataObj: Tenant | undefined): DetailItem[] => {
        if (!dataObj) return [];

        const detailItems: DetailItem[] = [];
        // prepare data to display in the details card
        Object.entries(dataObj).forEach(([key, value]) => {
          if (key === 'country') {
            detailItems.push({
              label: t('tenants.countryId.label'),
              value: (value as Country)?.code ?? '-',
            });
            return;
          }

          if (key === 'contractStartDate' || key === 'contractEndDate') {
            detailItems.push({
              label: t(`tenants.${key}.label`),
              value: value ? dayjs(value as Date).format('YYYY-MM-DD') : '-',
            });
            return;
          }

          if (key === 'createdAt' || key === 'updatedAt') {
            detailItems.push({
              label: t(`tenants.${key}.label`),
              value: formatDate(value as string) ?? '-',
            });
            return;
          }

          if (key === 'logo') {
            detailItems.push({
              label: t('tenants.logo.label'),
              value: value ? <MediaPreview mediaUrls={[value as string]} /> : '-',
            });
            return;
          }

          if (key === 'brandingColors') {
            const brandingColors = value as BrandingColors;
            detailItems.push({
              label: t(`tenants.${key}.label`),
              value: value ? (
                <>
                  <Tag color={brandingColors.primary}>Primary</Tag>
                  <Tag color={brandingColors.secondary}>Secondary</Tag>
                </>
              ) : (
                '-'
              ),
            });
            return;
          }

          detailItems.push({
            label: t(`tenants.${key}.label`),
            value: String(value),
          });
        });

        return detailItems;
      },
    [data?.data, t]
  );

  return (
    <>
      <PageTitle title={t('crud.details.title', { entity: t('tenants.tenant') })} />
      <HeaderRow
        title={t('crud.details.title', { entity: t('tenants.tenant') })}
        showBackButton={false}
        showEditButton={rbac.hasAccess(userPermissions, 'tenant.update')}
        editRoute={`/dashboard/tenant/${activeTenant?.id}/update`}
        trailTarget={id}
      />
      <div className="row mt-3">
        {(error || !activeTenant) && (
          <ErrorFallback
            message={t('tenants.noTenantSelected')}
            details={t('crud.details.errorLoadingData')}
            icon={<ExclamationCircleOutlined />}
            error={error}
            resetErrorBoundary={() => {}}
          />
        )}
        {activeTenant && !error && (
          <TenantDetailsComponent
            data={prepareDetailsData(data?.data)}
            success={data?.success ?? false}
            isLoading={isLoading}
            error={error}
          />
        )}
      </div>
    </>
  );
};

export default WithPermissionCheck(TenantDetailsContainer, 'tenant.view');
